import { useState } from "react";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { FileUpload } from "@/components/admin/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const EntityManager = ({ type, title, fields, labelField = "title", subLabelField }) => {
  const { data, refresh } = useData();
  const items = data[type] || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const emptyForm = () => {
    const f = {};
    fields.forEach((fl) => (f[fl.name] = fl.default ?? ""));
    return f;
  };

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.update(type, editing.id, form);
      else await api.create(type, form);
      await refresh();
      setOpen(false);
      toast.success("Sparat!");
    } catch (e) {
      toast.error("Kunde inte spara");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort?")) return;
    await api.remove(type, id);
    await refresh();
    toast.success("Borttaget");
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.reorder(type, arr.map((a) => a.id));
    await refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl font-black uppercase text-white">{title}</h2>
        <Button onClick={openNew} data-testid={`admin-add-${type}`} className="bg-amber-500 text-black hover:bg-amber-400">
          <Plus className="w-4 h-4 mr-1" /> Lägg till
        </Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-white/40 text-sm font-mono">Inget innehåll ännu.</p>}
        {items.map((item, idx) => (
          <div key={item.id} data-testid={`admin-row-${type}-${item.id}`} className="flex items-center gap-3 rounded-lg border border-white/8 bg-[#14161A] px-4 py-3">
            <div className="flex flex-col text-white/30">
              <button onClick={() => move(idx, -1)} className="hover:text-amber-500 text-[10px]">▲</button>
              <button onClick={() => move(idx, 1)} className="hover:text-amber-500 text-[10px]">▼</button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">{item[labelField] || item.name || item.question || item.heading || "(namnlös)"}</div>
              {subLabelField && <div className="text-xs text-white/40 truncate">{item[subLabelField]}</div>}
            </div>
            <button onClick={() => openEdit(item)} data-testid={`admin-edit-${item.id}`} className="p-2 text-white/50 hover:text-amber-500"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(item.id)} data-testid={`admin-delete-${item.id}`} className="p-2 text-white/50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#0F1114] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editing ? "Redigera" : "Ny"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((fl) => (
              <div key={fl.name}>
                <Label className="text-white/70 text-xs uppercase tracking-wide">{fl.label}</Label>
                <div className="mt-1.5">
                  {fl.type === "textarea" && <Textarea value={form[fl.name] || ""} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })} className="bg-black/40 border-white/15" rows={4} data-testid={`field-${fl.name}`} />}
                  {(fl.type === "text" || fl.type === "date" || !fl.type) && <Input value={form[fl.name] || ""} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })} className="bg-black/40 border-white/15" data-testid={`field-${fl.name}`} />}
                  {fl.type === "select" && (
                    <Select value={form[fl.name] ? form[fl.name] : "__none__"} onValueChange={(v) => setForm({ ...form, [fl.name]: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="bg-black/40 border-white/15" data-testid={`field-${fl.name}`}><SelectValue placeholder="Välj..." /></SelectTrigger>
                      <SelectContent>
                        {fl.optional && <SelectItem value="__none__">— Ingen —</SelectItem>}
                        {fl.options.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {fl.type === "image" && <FileUpload value={form[fl.name]} onChange={(url) => setForm({ ...form, [fl.name]: url })} kind="image" accept="image/*" />}
                  {fl.type === "file" && <FileUpload value={form[fl.name]} onChange={(url) => setForm({ ...form, [fl.name]: url })} kind="file" accept=".pdf,image/*" />}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/60">Avbryt</Button>
            <Button onClick={save} data-testid="admin-save" className="bg-amber-500 text-black hover:bg-amber-400">Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
